// src/components/FlashcardsBySubjectPage.js

import React, { useState, useEffect } from 'react';
import api from '../services/api'; // Importa o serviço de API configurado

// Componente para exibir um flashcard
const FlashcardItem = ({ flashcard, showVideo = true, onFlashcardClick }) => {
    const [virado, setVirado] = useState(false);

    // Se a função onFlashcardClick for fornecida, usamos o modo detalhado
    if (onFlashcardClick) {
        return (
            <div
                className="bg-white p-4 rounded-lg shadow-md border border-[#E0CCFF] mb-4 cursor-pointer hover:shadow-lg transition-all"
                onClick={() => onFlashcardClick(flashcard)}
            >
                <div className="flex justify-between items-start">
                    <div className="flex-1">
                        <p className="font-semibold text-gray-800">{flashcard.frente}</p>
                    </div>
                </div>

                {showVideo && flashcard.videoTitulo && (
                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                        <div className="font-medium text-yellow-800">Vídeo:</div>
                        <div className="text-gray-700 truncate">{flashcard.videoTitulo}</div>
                    </div>
                )}
            </div>
        );
    } else {
        // Versão com flip
        return (
            <div
                className={`bg-white p-4 rounded-lg shadow-md border border-[#E0CCFF] mb-4 cursor-pointer transition-all duration-300 ${
                    virado ? 'bg-violet-50' : ''
                }`}
                onClick={() => setVirado(!virado)}
            >
                <div className="flex justify-between items-start">
                    <div className="flex-1">
                        {virado ? (
                            <p className="text-gray-700">{flashcard.verso}</p>
                        ) : (
                            <p className="font-semibold text-gray-800">{flashcard.frente}</p>
                        )}
                    </div>
                </div>

                {showVideo && flashcard.videoTitulo && (
                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                        <div className="font-medium text-yellow-800">Vídeo:</div>
                        <div className="text-gray-700 truncate">{flashcard.videoTitulo}</div>
                    </div>
                )}

                <div className="mt-2 text-xs text-gray-500 text-center">
                    {virado ? 'Clique para ver a frente' : 'Clique para ver o verso'}
                </div>
            </div>
        );
    }
};

// Componente principal da página de flashcards por matéria
const FlashcardsBySubjectPage = ({ onVoltar, onNavigate, user }) => {
    const [materias, setMaterias] = useState([]);
    const [currentView, setCurrentView] = useState('materias'); // 'materias', 'topicos', 'subtopicos', 'flashcards'
    const [selectedMateria, setSelectedMateria] = useState(null);
    const [selectedTopico, setSelectedTopico] = useState(null);
    const [selectedSubtopico, setSelectedSubtopico] = useState(null);
    const [selectedFlashcard, setSelectedFlashcard] = useState(null);
    const [showFlashcardDetail, setShowFlashcardDetail] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hasNavigatedWithinSection, setHasNavigatedWithinSection] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);
    const [accessChecked, setAccessChecked] = useState(false);


    useEffect(() => {
        const checkAccessAndLoadData = async () => {
            try {
                // Check if user has access to flashcards section
                const accessResponse = await api.get('/api/section-access/check/flashcards');
                if (accessResponse.data.success && accessResponse.data.hasAccess) {
                    setHasAccess(true);

                    const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                    if (!userInfo || !userInfo.token) {
                        setError('Usuário não autenticado. Faça login novamente.');
                        setLoading(false);
                        setAccessChecked(true);
                        return;
                    }

                    const config = {
                        headers: { Authorization: `Bearer ${userInfo.token}` }
                    };

                    // Buscar flashcards organizados por matérias e tópicos
                    const response = await api.get('/api/flashcards/organizados', config);
                    setMaterias(response.data.materias || []);
                } else {
                    setHasAccess(false);
                }
            } catch (err) {
                console.error('Erro ao verificar acesso ou carregar flashcards organizados:', err);
                setHasAccess(false);
            } finally {
                setLoading(false);
                setAccessChecked(true);
            }
        };

        checkAccessAndLoadData();
    }, [user]);

    // Função para lidar com o upgrade
    const handleUpgrade = () => {
        // Redirecionar para a página de vendas
        onVoltar('venda-premium');
    };

    if (!accessChecked) {
        return (
            <div className="min-h-screen bg-[#F8F6FC] flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#9B4DFF] mx-auto mb-4"></div>
                    <p className="text-gray-600">Verificando acesso...</p>
                </div>
            </div>
        );
    }

    if (!hasAccess) {
        return (
            <div className="min-h-screen bg-[#F8F6FC]">
                <div className="container mx-auto p-4 sm:p-6 md:p-8">
                    {/* Header */}
                    <header className="flex items-center justify-between mb-8">
                        <div className="flex items-center">
                            <button
                                onClick={() => {
                                    if (hasNavigatedWithinSection && currentView !== 'materias') {
                                        // If we've navigated within the section, go back to materials view
                                        setCurrentView('materias');
                                        setSelectedMateria(null);
                                        setSelectedTopico(null);
                                        setSelectedSubtopico(null);
                                        setSelectedFlashcard(null);
                                        setHasNavigatedWithinSection(false);
                                    } else {
                                        // Otherwise, go back to the previous page
                                        onVoltar();
                                    }
                                }}
                                className="flex items-center text-[#7B2CDB] font-semibold hover:text-[#9B4DFF] transition-colors mr-6"
                            >
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                                </svg>
                                Voltar
                            </button>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-800">Por Matéria</h1>
                                {/* Breadcrumbs */}
                                <div className="flex items-center text-sm text-gray-500 mt-1">
                                    <button
                                        onClick={() => setCurrentView('materias')}
                                        className="hover:text-[#7B2CDB] transition-colors"
                                    >
                                        Início
                                    </button>
                                    {selectedMateria && (
                                        <>
                                            <span className="mx-2">›</span>
                                            <button
                                                onClick={() => setCurrentView('topicos')}
                                                className="hover:text-[#7B2CDB] transition-colors"
                                            >
                                                {selectedMateria.nome}
                                            </button>
                                        </>
                                    )}
                                    {selectedTopico && (
                                        <>
                                            <span className="mx-2">›</span>
                                            <button
                                                onClick={() => setCurrentView('subtopicos')}
                                                className="hover:text-[#7B2CDB] transition-colors"
                                            >
                                                {selectedTopico.nome}
                                            </button>
                                        </>
                                    )}
                                    {selectedSubtopico && (
                                        <>
                                            <span className="mx-2">›</span>
                                            <button
                                                onClick={() => setCurrentView('flashcards')}
                                                className="hover:text-[#7B2CDB] transition-colors"
                                            >
                                                {selectedSubtopico.nome}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </header>

                    {/* Conteúdo principal com blur */}
                    <div className="blur-sm pointer-events-none">
                        {/* Navegação baseada na view atual */}
                        {currentView === 'materias' && (
                            <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 mb-6">
                                <div className="flex justify-between items-center">
                                    <p className="text-violet-800">
                                        <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                        Selecione uma matéria para ver seus flashcards organizados por tópicos.
                                    </p>
                                    {onNavigate && (
                                        <button
                                            className="bg-violet-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-violet-700 transition-colors"
                                        >
                                            Revisar Flashcards
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                        {currentView === 'materias' && renderMateriasView()}
                        {currentView === 'topicos' && renderTopicosView()}
                        {currentView === 'subtopicos' && renderSubtopicosView()}
                        {currentView === 'flashcards' && renderFlashcardsView()}
                    </div>

                    {/* Overlay com mensagem de acesso */}
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="text-center p-8 max-w-md bg-white rounded-xl shadow-lg">
                            <h2 className="text-2xl font-bold text-purple-600 mb-4">É necessário atualizar o seu plano para ter acesso</h2>
                            <div className="mb-4">
                                <button
                                    onClick={handleUpgrade}
                                    className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-3 px-6 rounded-full transition-colors duration-300 shadow-lg"
                                >
                                    Atualize seu Plano
                                </button>
                            </div>
                            <button
                              onClick={() => window.history.back()}
                              className="text-gray-600 hover:text-gray-800 font-medium text-sm underline"
                            >
                              Voltar
                            </button>
                        </div>
                    </div>
                </div>

                {/* AdSense ad after the main content */}

                {/* Modal para detalhes do flashcard */}
                <FlashcardDetailModal />
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F8F6FC] flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#9B4DFF] mx-auto mb-4"></div>
                    <p className="text-gray-600">Carregando seus flashcards...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-[#F8F6FC] flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-500 mb-4">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-[#9B4DFF] text-white px-4 py-2 rounded-lg hover:bg-[#7B2CDB] transition-colors"
                    >
                        Tentar Novamente
                    </button>
                </div>
            </div>
        );
    }

    // Componente para mostrar as matérias
    const renderMateriasView = () => {
        if (materias.length === 0) {
            return (
                <div className="bg-white p-8 rounded-xl shadow-md border border-[#E0CCFF] text-center">
                    <div className="mb-4">
                        <svg className="w-16 h-16 text-gray-300 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                        </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">
                        Nenhuma matéria com flashcards
                    </h3>
                    <p className="text-gray-500 mb-6">
                        Você ainda não criou nenhum flashcard. Crie flashcards dentro das videoaulas!
                    </p>
                </div>
            );
        }

        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {materias.map((materia) => (
                    <div
                        key={materia._id}
                        className="bg-white rounded-xl shadow-md border border-[#E0CCFF] hover:shadow-lg transition-shadow cursor-pointer overflow-hidden"
                        onClick={() => {
                            setSelectedMateria(materia);
                            setCurrentView('topicos');
                            setHasNavigatedWithinSection(true);
                        }}
                    >
                        {materia.imagem ? (
                            <img
                                src={materia.imagem}
                                alt={materia.nome}
                                className="w-full h-40 object-cover"
                            />
                        ) : (
                            <div className="w-full h-40 bg-violet-100 flex items-center justify-center">
                                <span className="text-violet-600 font-bold text-4xl">
                                    {materia.nome.charAt(0)}
                                </span>
                            </div>
                        )}
                        <div className="p-4">
                            <h3 className="font-bold text-lg text-center text-gray-800">{materia.nome}</h3>
                            <p className="text-sm text-gray-500 text-center mt-1">
                                {materia.topicos.reduce((total, topico) => total + topico.subtopicos.reduce((subTotal, subtopico) => subTotal + subtopico.flashcards.length, 0), 0)} flashcards
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Componente para mostrar os tópicos de uma matéria
    const renderTopicosView = () => {
        if (!selectedMateria || selectedMateria.topicos.length === 0) {
            return (
                <div className="bg-white p-8 rounded-xl shadow-md border border-[#E0CCFF] text-center">
                    <div className="mb-4">
                        <svg className="w-16 h-16 text-gray-300 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                        </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">
                        Nenhum tópico com flashcards
                    </h3>
                    <p className="text-gray-500 mb-6">
                        Não há tópicos com flashcards nesta matéria.
                    </p>
                    <button
                        onClick={() => setCurrentView('materias')}
                        className="bg-[#9B4DFF] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#7B2CDB] transition-colors"
                    >
                        Voltar para Matérias
                    </button>
                </div>
            );
        }

        return (
            <div>
                <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">{selectedMateria.nome}</h2>
                    <p className="text-gray-600">Selecione um tópico para ver os flashcards</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedMateria.topicos.map((topico) => {
                        const totalFlashcards = topico.subtopicos.reduce((total, subtopico) => total + subtopico.flashcards.length, 0);
                        return (
                            <div
                                key={topico._id}
                                className={`bg-white p-6 rounded-xl shadow-md border ${
                                    totalFlashcards > 0 ? 'border-[#E0CCFF] hover:shadow-lg cursor-pointer hover:bg-violet-50' : 'border-gray-200 opacity-60'
                                }`}
                                onClick={() => {
                                    if (totalFlashcards > 0) {
                                        setSelectedTopico(topico);
                                        setCurrentView('subtopicos');
                                        setHasNavigatedWithinSection(true);
                                    }
                                }}
                            >
                                <div className="flex justify-between items-center">
                                    <h3 className="font-bold text-lg text-gray-800">{topico.nome}</h3>
                                    <span className="bg-violet-100 text-violet-800 text-sm font-semibold px-3 py-1 rounded-full">
                                        {totalFlashcards} flashcards
                                    </span>
                                </div>
                                {totalFlashcards === 0 && (
                                    <p className="text-gray-500 text-sm mt-2">Nenhum flashcard disponível</p>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Componente para mostrar os subtopicos de um tópico
    const renderSubtopicosView = () => {
        if (!selectedTopico || selectedTopico.subtopicos.length === 0) {
            return (
                <div className="bg-white p-8 rounded-xl shadow-md border border-[#E0CCFF] text-center">
                    <div className="mb-4">
                        <svg className="w-16 h-16 text-gray-300 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"></path>
                        </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">
                        Nenhum subtópico com flashcards
                    </h3>
                    <p className="text-gray-500 mb-6">
                        Não há subtópicos com flashcards neste tópico.
                    </p>
                    <button
                        onClick={() => setCurrentView('topicos')}
                        className="bg-[#9B4DFF] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#7B2CDB] transition-colors mr-2"
                    >
                        Voltar para Tópicos
                    </button>
                    <button
                        onClick={() => setCurrentView('materias')}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-400 transition-colors"
                    >
                        Voltar para Matérias
                    </button>
                </div>
            );
        }

        return (
            <div>
                <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">{selectedTopico.nome}</h2>
                    <p className="text-gray-600">Selecione um subtópico para ver os flashcards</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedTopico.subtopicos.map((subtopico) => (
                        <div
                            key={subtopico._id}
                            className={`bg-white p-6 rounded-xl shadow-md border ${
                                subtopico.flashcards.length > 0 ? 'border-[#E0CCFF] hover:shadow-lg cursor-pointer hover:bg-violet-50' : 'border-gray-200 opacity-60'
                            }`}
                            onClick={() => {
                                if (subtopico.flashcards.length > 0) {
                                    setSelectedSubtopico(subtopico);
                                    setCurrentView('flashcards');
                                    setHasNavigatedWithinSection(true);
                                }
                            }}
                        >
                            <div className="flex justify-between items-center">
                                <h3 className="font-bold text-lg text-gray-800">{subtopico.nome}</h3>
                                <span className="bg-violet-100 text-violet-800 text-sm font-semibold px-3 py-1 rounded-full">
                                    {subtopico.flashcards.length} flashcards
                                </span>
                            </div>
                            {subtopico.flashcards.length === 0 && (
                                <p className="text-gray-500 text-sm mt-2">Nenhum flashcard disponível</p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Componente para mostrar os flashcards de um subtópico
    const renderFlashcardsView = () => {
        if (!selectedSubtopico || selectedSubtopico.flashcards.length === 0) {
            return (
                <div className="bg-white p-8 rounded-xl shadow-md border border-[#E0CCFF] text-center">
                    <div className="mb-4">
                        <svg className="w-16 h-16 text-gray-300 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                        </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">
                        Nenhum flashcard encontrado
                    </h3>
                    <p className="text-gray-500 mb-6">
                        Não há flashcards para este subtópico.
                    </p>
                    <button
                        onClick={() => setCurrentView('subtopicos')}
                        className="bg-[#9B4DFF] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#7B2CDB] transition-colors mr-2"
                    >
                        Voltar para Subtópicos
                    </button>
                    <button
                        onClick={() => setCurrentView('materias')}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg font-medium hover:bg-gray-400 transition-colors"
                    >
                        Voltar para Matérias
                    </button>
                </div>
            );
        }

        return (
            <div>
                <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">{selectedSubtopico.nome}</h2>
                    <p className="text-gray-600">Total de {selectedSubtopico.flashcards.length} flashcards</p>
                </div>

                <div>
                    {selectedSubtopico.flashcards.map((flashcard) => (
                        <FlashcardItem
                            key={flashcard._id}
                            flashcard={flashcard}
                            showVideo={true}
                            onFlashcardClick={(flashcard) => {
                                setSelectedFlashcard({
                                    ...flashcard,
                                    frente: flashcard.frente || '',
                                    verso: flashcard.verso || '',
                                    virado: false  // Inicializa o estado de virado como falso
                                });
                                setShowFlashcardDetail(true);
                            }}
                        />
                    ))}
                </div>
            </div>
        );
    };

    // Modal para detalhes do flashcard
    const FlashcardDetailModal = () => {
        if (!showFlashcardDetail || !selectedFlashcard) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <style>{`
                    .scene {
                        perspective: 1000px;
                    }
                    .card {
                        position: relative;
                        width: 100%;
                        height: 260px; /* ajuste conforme seu layout */
                        transform-style: preserve-3d;
                        transition: transform 0.6s ease;
                    }
                    .card.is-flipped {
                        transform: rotateY(180deg);
                    }
                    .face {
                        position: absolute;
                        inset: 0;
                        backface-visibility: hidden;
                    }
                    .face.back {
                        transform: rotateY(180deg);
                    }
                `}</style>
                <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="text-xl font-bold text-gray-800">Detalhes do Flashcard</h3>
                            <button
                                onClick={() => setShowFlashcardDetail(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>

                        {/* Componente de flashcard com animação de flip */}
                        <div className="scene mx-auto">
                            <div className={`card ${selectedFlashcard.virado ? 'is-flipped' : ''}`}
                                onClick={() => {
                                    setSelectedFlashcard(prev => ({
                                        ...prev,
                                        virado: !prev.virado
                                    }));
                                }}
                            >
                                {/* Frente do flashcard */}
                                <div className="face front">
                                    <div className="p-6 border-2 border-dashed border-violet-300 rounded-lg bg-gradient-to-br from-violet-50 to-purple-50 h-full flex flex-col">
                                        <h4 className="font-semibold text-gray-700 mb-3">Frente:</h4>
                                        <p className="text-gray-800 flex-grow break-words">
                                            {selectedFlashcard.frente && selectedFlashcard.frente.trim() ? selectedFlashcard.frente : 'Nenhuma informação na frente do flashcard'}
                                        </p>
                                    </div>
                                </div>

                                {/* Verso do flashcard */}
                                <div className="face back">
                                    <div className="p-6 border-2 border-dashed border-violet-300 rounded-lg bg-gradient-to-br from-violet-50 to-purple-50 h-full flex flex-col">
                                        <h4 className="font-semibold text-gray-700 mb-3">Verso:</h4>
                                        <p className="text-gray-800 flex-grow break-words">
                                            {selectedFlashcard.verso && selectedFlashcard.verso.trim() ? selectedFlashcard.verso : 'Nenhuma informação no verso do flashcard'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Instruções para flip */}
                        <div className="text-center mt-4 text-sm text-gray-600 italic">
                            {selectedFlashcard.virado
                                ? 'Você está vendo o verso do flashcard'
                                : 'Clique no flashcard para virar e ver o verso'}
                        </div>

                        {selectedFlashcard.videoTitulo && (
                            <div className="mt-6">
                                <h4 className="font-semibold text-gray-700 mb-2">Vídeo Relacionado:</h4>
                                <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                                    <p className="font-medium text-yellow-800">{selectedFlashcard.videoTitulo}</p>
                                    {selectedFlashcard.videoUrl && (
                                        <div className="mt-2">
                                            <a
                                                href={selectedFlashcard.videoUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:text-blue-800 underline inline-block"
                                            >
                                                Assistir ao vídeo
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={() => setShowFlashcardDetail(false)}
                                className="bg-[#9B4DFF] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#7B2CDB] transition-colors"
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-[#F8F6FC]">
            <div className="container mx-auto p-4 sm:p-6 md:p-8">
                {/* Header */}
                <header className="flex items-center justify-between mb-8">
                    <div className="flex items-center">
                        <button
                            onClick={() => {
                                if (hasNavigatedWithinSection && currentView !== 'materias') {
                                    // If we've navigated within the section, go back to materials view
                                    setCurrentView('materias');
                                    setSelectedMateria(null);
                                    setSelectedTopico(null);
                                    setSelectedSubtopico(null);
                                    setSelectedFlashcard(null);
                                    setHasNavigatedWithinSection(false);
                                } else {
                                    // Otherwise, go back to the previous page
                                    onVoltar();
                                }
                            }}
                            className="flex items-center text-[#7B2CDB] font-semibold hover:text-[#9B4DFF] transition-colors mr-6"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
                            </svg>
                            Voltar
                        </button>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Por Matéria</h1>
                            {/* Breadcrumbs */}
                            <div className="flex items-center text-sm text-gray-500 mt-1">
                                <button
                                    onClick={() => setCurrentView('materias')}
                                    className="hover:text-[#7B2CDB] transition-colors"
                                >
                                    Início
                                </button>
                                {selectedMateria && (
                                    <>
                                        <span className="mx-2">›</span>
                                        <button
                                            onClick={() => setCurrentView('topicos')}
                                            className="hover:text-[#7B2CDB] transition-colors"
                                        >
                                            {selectedMateria.nome}
                                        </button>
                                    </>
                                )}
                                {selectedTopico && (
                                    <>
                                        <span className="mx-2">›</span>
                                        <button
                                            onClick={() => setCurrentView('subtopicos')}
                                            className="hover:text-[#7B2CDB] transition-colors"
                                        >
                                            {selectedTopico.nome}
                                        </button>
                                    </>
                                )}
                                {selectedSubtopico && (
                                    <>
                                        <span className="mx-2">›</span>
                                        <button
                                            onClick={() => setCurrentView('flashcards')}
                                            className="hover:text-[#7B2CDB] transition-colors"
                                        >
                                            {selectedSubtopico.nome}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                {/* Navegação baseada na view atual */}
                {currentView === 'materias' && (
                    <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 mb-6">
                        <div className="flex justify-between items-center">
                            <p className="text-violet-800">
                                <svg className="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                                Selecione uma matéria para ver seus flashcards organizados por tópicos.
                            </p>
                            {onNavigate && (
                                <button
                                    onClick={() => onNavigate('flashcard-revision')}
                                    className="bg-violet-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-violet-700 transition-colors"
                                >
                                    Revisar Flashcards
                                </button>
                            )}
                        </div>
                    </div>
                )}
                {currentView === 'materias' && renderMateriasView()}
                {currentView === 'topicos' && renderTopicosView()}
                {currentView === 'subtopicos' && renderSubtopicosView()}
                {currentView === 'flashcards' && renderFlashcardsView()}
            </div>

            {/* AdSense ad after the main content */}

            {/* Modal para detalhes do flashcard */}
            <FlashcardDetailModal />
        </div>
    );
};

export default FlashcardsBySubjectPage;